import { LinkingMailboxModel } from '../LinkingMailboxModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import { tradingViewChartWidgetStateSchema } from './state';
import type { TradingViewChartWidgetState } from './state';
import { effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { getUnderlyingSymbolFromInstrument, parseInstrument } from 'src/lib/instrumentUtils';
import type { TradingViewChartWidgetModel } from 'src/models/TradingViewChartWidgetModel';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { LinkEvent } from 'src/services/WorkspaceService/linking';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { WidgetContainerChannelSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import { WidgetContainerTabSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

const ACCEPTED_INSTRUMENT_SOURCES = new Set<WidgetType>([
    'results',
    'watchlist',
    'order-blotter',
    'trade-pro',
    'trade-pro-multi',
    'positions',
    'executions',
    'orders',
    'lab',
    'alpha-lens',
    'alpha-eye',
    'searchalpha',
    'scanner',
    'screener',
    'news',
    'events',
    'filings',
    'tradingview-chart',
]);

@injectable()
export class TradingViewChartWidgetModelImpl implements TradingViewChartWidgetModel {
    static readonly stateSchema = tradingViewChartWidgetStateSchema;
    static defaultState(): TradingViewChartWidgetState {
        return { symbol: 'SPY', interval: '1D', timezone: 'exchange' };
    }
    static fromJSON(container: ReactWidgetContainer): TradingViewChartWidgetModelImpl {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        container
            .bind('InitialWidgetState')
            .toConstantValue(decodeSelfOwnedWidgetState(this.stateSchema, getInitialData()));
        return container.get('TradingViewChartWidgetModel') as TradingViewChartWidgetModelImpl;
    }

    readonly #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);
    readonly #instrument: Signal<string> = signal('');
    readonly #chartState: Signal<object | undefined> = signal(undefined);
    readonly #interval: Signal<string | undefined> = signal(undefined);
    readonly #timezone: Signal<string | undefined> = signal(undefined);
    readonly #dirty = signal(false);
    readonly #disposableStack = new DisposableStack();
    readonly #workspaceService?: WorkspaceService;
    readonly #tabId?: WidgetId;
    readonly #logger: Logger;
    readonly headerElements: WidgetHeaderElement[];
    readonly eventBus: TradingViewChartWidgetModel['eventBus'];

    constructor(
        @inject('WorkspaceService') @optional() workspaceService: WorkspaceService | undefined,
        @inject('WidgetId') @optional() tabId: WidgetId | undefined,
        @inject('WidgetContainerController') @optional() container: WidgetContainerController | undefined,
        @inject('InitialWidgetState') initialState: TradingViewChartWidgetState,
        @inject('Logger') logger: Logger,
    ) {
        this.#workspaceService = workspaceService;
        this.#tabId = tabId;
        this.#logger = logger;
        this.#instrument.value = initialState.symbol;
        this.#chartState.value = initialState.chartState;
        this.#interval.value = initialState.interval;
        this.#timezone.value = initialState.timezone;
        const hasWorkspaceContext = workspaceService !== undefined && tabId !== undefined && container !== undefined;
        if (
            !hasWorkspaceContext &&
            (workspaceService !== undefined || tabId !== undefined || container !== undefined)
        ) {
            throw new Error(
                'TradingView workspace model requires WorkspaceService, WidgetId, and WidgetContainerController',
            );
        }
        if (hasWorkspaceContext) {
            const channel = new WidgetContainerChannelSlice({ container, tabId });
            const tab = new WidgetContainerTabSlice({ container, tabId, logger });
            this.eventBus = MergedEventBus.from(this.#ownBus())
                .merge(channel.eventBus)
                .merge(tab.eventBus) as TradingViewChartWidgetModel['eventBus'];
            this.headerElements = [
                ...COMMON_HEADER_ELEMENTS,
                { type: 'symbolPicker', position: 30, priority: 5, variant: 'light' },
            ];
            const mailbox = new LinkingMailboxModel({
                workspaceService,
                acceptsLinkEvent: (event: LinkEvent) => {
                    const channelId = channel.channel.value;
                    return (
                        channelId !== null &&
                        event.channelId === channelId &&
                        event.type === 'instrument-selected' &&
                        (event.from.kind === 'external'
                            ? event.from.integrationId === 'das-trader'
                            : event.from.tabId !== tabId && ACCEPTED_INSTRUMENT_SOURCES.has(event.from.widgetType))
                    );
                },
            });
            this.#disposableStack.defer(() => mailbox.dispose());
            this.#disposableStack.defer(
                effect(() => {
                    const linked = mailbox.instrument.value;
                    if (linked) untracked(() => this.#setInstrumentFromLink(linked.value));
                }),
            );
        } else {
            this.eventBus = this.#ownBus() as TradingViewChartWidgetModel['eventBus'];
            this.headerElements = [
                ...STANDALONE_COMMON_HEADER_ELEMENTS,
                { type: 'symbolPicker', position: 30, priority: 5, variant: 'light' },
            ];
        }
    }

    #ownBus() {
        return EventBusBuilder.create()
            .addEvent('widget::instrumentChange', ({ instrument }) => this.setInstrument(instrument))
            .addSignal('widget::instrument', () => this.#instrument)
            .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
            .build();
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#dirty;
    }
    get instrument(): ReadonlySignal<string> {
        return this.#instrument;
    }
    get chartState(): ReadonlySignal<object | undefined> {
        return this.#chartState;
    }
    get interval(): ReadonlySignal<string | undefined> {
        return this.#interval;
    }
    get timezone(): ReadonlySignal<string | undefined> {
        return this.#timezone;
    }
    toJSON(): TradingViewChartWidgetState {
        return {
            symbol: this.#instrument.value,
            chartState: this.#chartState.value,
            interval: this.#interval.value,
            timezone: this.#timezone.value,
        };
    }
    markClean(): void {
        this.#dirty.value = false;
    }
    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    #setInstrumentFromLink(instrument: string): void {
        try {
            const symbol = getUnderlyingSymbolFromInstrument(parseInstrument(instrument));
            if (symbol === this.#instrument.value) return;
            this.#instrument.value = symbol;
            this.#dirty.value = true;
        } catch {
            // Unsupported instruments cannot be displayed by this equity chart.
        }
    }
    setInstrument(instrument: string): void {
        if (instrument === this.#instrument.value) return;
        this.#instrument.value = instrument;
        this.#dirty.value = true;
        if (this.#tabId !== undefined && this.#workspaceService !== undefined) {
            void this.#workspaceService
                .publishLinkIntent({
                    type: 'instrument-selected',
                    fromTabId: this.#tabId,
                    details: { instrument },
                })
                .catch((error: unknown) => {
                    this.#logger.error('Failed to publish TradingView chart link intent', {
                        error,
                        instrument,
                        tabId: this.#tabId,
                    });
                });
        }
    }
    updateChartState(chartState: object, interval: string, timezone: string): void {
        this.#chartState.value = chartState;
        this.#interval.value = interval;
        this.#timezone.value = timezone;
        this.#dirty.value = true;
    }
}
